'use client';

import { create } from 'zustand';
import type { AuthUser } from '@/types/auth';
import type { AppCapabilities } from '@/types/actions';
import type { SubscriptionContext } from '@/types/subscription';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  capabilities: AppCapabilities | null;
  /** Compact entitlement snapshot for the selected business (merchants only). */
  subscription: SubscriptionContext | null;
  isAuthenticated: boolean;

  setAuth: (user: AuthUser, token: string) => void;
  setUser: (user: AuthUser) => void;
  setCapabilities: (caps: AppCapabilities) => void;
  setSubscription: (subscription: SubscriptionContext | null) => void;
  clearAuth: () => void;

  /** Layer 1: static permission check (superadmin OR user.permissions[]) */
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;

  /** Layer 1: capability check (nav/page-level, from /auth/me/capabilities) */
  can: (capability: keyof AppCapabilities) => boolean;

  /** Layer 2: plan entitlements (UX only — backend is authoritative) */
  hasFeature: (feature: string) => boolean;
  featureLimit: (feature: string) => number | null | undefined;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  capabilities: null,
  subscription: null,
  isAuthenticated: false,

  setAuth: (user, token) => set({ user, token, isAuthenticated: true }),

  setUser: (user) => set({ user }),

  setCapabilities: (capabilities) => set({ capabilities }),

  setSubscription: (subscription) => set({ subscription }),

  clearAuth: () => set({ user: null, token: null, capabilities: null, subscription: null, isAuthenticated: false }),

  hasPermission: (permission) => {
    const { user } = get();
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return user.permissions.includes(permission);
  },

  hasAnyPermission: (permissions) => {
    const { hasPermission } = get();
    return permissions.some((p) => hasPermission(p));
  },

  can: (capability) => {
    const { user, capabilities } = get();
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    // Backend may not yet return every key — treat missing as false
    return (capabilities as Record<string, boolean> | null)?.[capability as string] === true;
  },

  hasFeature: (feature) => {
    const { subscription } = get();
    if (!subscription) return false;
    const value = subscription.features[feature];
    return value !== undefined && value !== 0;
  },

  featureLimit: (feature) => {
    const { subscription } = get();
    if (!subscription) return undefined;
    const value = subscription.features[feature];
    if (value === undefined) return 0;
    return value === true ? null : value;
  },
}));

